import { Directive, ElementRef, OnDestroy, OnInit } from '@angular/core';

/**
 * @directive DotCmsColumnClassCleanupDirective
 *
 * @description
 * A utility directive that monitors dotCMS column components and removes
 * duplicate CSS grid classes (e.g., `col-start-*` / `col-end-*`) that can appear
 * after Angular SSR hydration or SDK re-renders.
 *
 * The directive uses a MutationObserver to watch the DOM for changes within
 * the host element and ensures that only the latest positioning classes are kept.
 *
 * @usage
 * ```html
 * <dotcms-column
 *   [column]="column"
 *   dotCmsColumnClassCleanup>
 * </dotcms-column>
 * ```
 *
 * @safety
 * - SSR-safe (does not run in non-browser environments)
 * - Disconnects MutationObserver on destroy
 * - Debounced cleanup to avoid performance issues on rapid DOM updates
 *
 * @author Thanigaivel
 */
@Directive({
  selector: '[dotCmsColumnClassCleanup]',
})
export class DotCmsColumnClassCleanupDirective implements OnInit, OnDestroy {
  private observer?: MutationObserver;
  private cleanupTimer?: number;

  constructor(private el: ElementRef) {}

  ngOnInit() {
    // Skip execution during SSR or if MutationObserver is not available
    if (typeof window === 'undefined' || typeof MutationObserver === 'undefined') {
      return;
    }
    if (!(this.el.nativeElement instanceof HTMLElement)) {
      return;
    }

    // Observe changes within the host element
    this.observer = new MutationObserver(() => {
      clearTimeout(this.cleanupTimer);
      // Debounce cleanup to avoid redundant runs
      this.cleanupTimer = window.setTimeout(() => this.runCleanup(), 50);
    });

    this.observer.observe(this.el.nativeElement, {
      childList: true,
      subtree: true,
    });
  }

  /**
   * Runs cleanup logic to remove older duplicate col-start/col-end classes.
   */
  private runCleanup() {
    const elements = this.el.nativeElement.querySelectorAll('[class*="col-end-"], [class*="col-start-"]');
    elements.forEach((el: HTMLElement) => {
      this.cleanupClasses(el, 'col-end-');
      this.cleanupClasses(el, 'col-start-');
    });
  }

  /**
   * Removes duplicate classes that share the same prefix.
   * Keeps the latest (last) class instance to ensure the correct layout.
   */
  private cleanupClasses(el: HTMLElement, prefix: string): void {
    const matched = Array.from(el.classList).filter(c => c.startsWith(prefix));
    if (matched.length > 1) {
      el.classList.remove(matched[0]); // Remove the older duplicate
    }
  }

  ngOnDestroy() {
    // Clean up observer and timers
    this.observer?.disconnect();
    clearTimeout(this.cleanupTimer);
  }
}
